import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import {
  Dropdown,
  ListGroup,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { Option } from "react-select/src/filters";
import { Icon } from "@/components";
import { PartnerBookingPolicy, PartnerBookingHour } from "@/data/partners";
import { useI18n } from "@/lib/i18n/i18n_provider";
import { Spacer } from "@/offering/spacer";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import styles from "./gallery_mobile_filters.module.css";
import { FilterId, MobileFilter } from "./mobile_filters";
import {
  useLocationDropdownFeatureFlag,
  useMrGalleryDateTimeFilterFeatureFlag,
  useCapacityFilterFeatureFlag,
} from "@/core/splitio";
import { LocationFilter, LocationFilterMobile } from "./location_filter";
import { FilterLocation, Partner } from "@/data/partner_offering";
import { ModalCapacityFilterItem } from "./capacity_filter_v2";
import { useLocationParam } from "../../hooks/use_location_param";
import { useLocationPickerState } from "../../hooks/use_location_picker_state";
import { CapacityFilterV2 } from "./capacity_filter_v2";
import {
  MobileDateTimeFilterItem,
  MobileDateTimeFilterItemV2,
  MobileModalDateTimeFilterItem,
  MobileModalDateTimeFilterItemV2,
} from "./mobile_date_time_filter_item";
import { DateTimeValue } from "../date-time-picker/date_time_picker";

interface GalleryMobileFiltersProps {
  partner: Partner;
  filters: MobileFilter;
  filterValues: MeetingRoomsFilter;
  locations: FilterLocation[];
  onFilterValuesChange: (filter: MeetingRoomsFilter) => void;
  totalRooms: number;
  foundResults: number;
  partnerBookingPolicy?: PartnerBookingPolicy;
  partnerBookingHour?: PartnerBookingHour;
  maxCapacity: number;
  onContactUs?: () => void;
}

export function GalleyMobileFilters({
  partner,
  filters,
  filterValues,
  locations,
  onFilterValuesChange,
  totalRooms,
  foundResults,
  partnerBookingHour,
  partnerBookingPolicy,
  maxCapacity,
  onContactUs,
}: GalleryMobileFiltersProps) {
  const { fbt } = useI18n();
  const locationDropdownFlag = useLocationDropdownFeatureFlag();
  const capacityFilterFlag = useCapacityFilterFeatureFlag();
  const { cities, locationIDs } = useLocationParam(locations);
  const mrGalleryDateTimeFilterFlag = useMrGalleryDateTimeFilterFeatureFlag();

  const [highlightLocation, setHighlightLocation] = useState(false);

  const {
    displayedValue,
    selectedCities,
    selectedLocationIDs,
    handleSelectCity,
    handleSelectLocation,
    handleClearSelection,
  } = useLocationPickerState(locations, {
    cities,
    locationIDs,
  });

  const [showModal, setShowModal] = useState(false);
  const [internalFilterValues, setInternalFilterValues] =
    useState(filterValues);

  const locationOptions = useMemo(() => {
    let filteredLocations = filters.location?.options || [];
    if (internalFilterValues.state) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.state === internalFilterValues.state
      );
    }
    if (internalFilterValues.city) {
      filteredLocations = filteredLocations.filter(
        (o) => o.data.city === internalFilterValues.city
      );
    }
    return filteredLocations;
  }, [filters, internalFilterValues]);

  const cityOptions = useMemo(() => {
    let filteredCities = filters.city?.options || [];
    if (internalFilterValues.state) {
      filteredCities = filteredCities.filter(
        (o) => o.data.state === internalFilterValues.state
      );
    }
    return filteredCities;
  }, [filters, internalFilterValues]);

  const filterKeys = Object.keys(filters) as FilterId[];
  const numberOfFilters = filterKeys.length;

  useEffect(() => {
    if (numberOfFilters > 0) {
      setInternalFilterValues(filterValues);
    }
  }, [filterValues, numberOfFilters]);

  if (numberOfFilters === 0) {
    return <></>;
  }

  let content: JSX.Element;

  if (
    numberOfFilters < 2 ||
    (numberOfFilters < 3 &&
      filterKeys.includes(FilterId.City) &&
      filterKeys.includes(FilterId.Location))
  ) {
    const disableDateTimeFilter =
      !selectedCities.length &&
      !selectedLocationIDs.length &&
      locations.length > 2 &&
      !!filters.location;

    content = (
      <>
        {locationDropdownFlag && filters.location ? (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <LocationFilter
              partner={partner}
              filters={filters}
              locations={locations}
              displayedValue={displayedValue}
              values={{
                cities: selectedCities,
                locationIDs: selectedLocationIDs,
              }}
              onClearSelection={handleClearSelection}
              onSelectCity={handleSelectCity}
              onSelectLocation={handleSelectLocation}
            />
          </div>
        ) : (
          <>
            {filters.state && (
              <div
                className={
                  numberOfFilters > 1
                    ? styles.mobileFilterItemWrapper
                    : styles.singleMobileFilterItemWrapper
                }
              >
                <MobileFilterItem
                  value={filters.state.value}
                  onSelect={filters.state.onChange}
                  options={filters.state.options}
                  label={filters.state.label}
                />
              </div>
            )}
            {filters.city && (
              <div
                className={
                  numberOfFilters > 1
                    ? styles.mobileFilterItemWrapper
                    : styles.singleMobileFilterItemWrapper
                }
              >
                <MobileFilterItem
                  value={filters.city.value}
                  onSelect={filters.city.onChange}
                  options={filters.city.options}
                  label={filters.city.label}
                />
              </div>
            )}
            {filters.location && (
              <div
                className={
                  numberOfFilters > 1
                    ? styles.mobileFilterItemWrapper
                    : styles.singleMobileFilterItemWrapper
                }
              >
                <MobileFilterItem
                  value={filters.location.value}
                  onSelect={filters.location.onChange}
                  options={filters.location.options}
                  label={filters.location.label}
                />
              </div>
            )}
          </>
        )}
        {filters.layout && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            <MobileFilterItem
              value={filters.layout.value}
              onSelect={filters.layout.onChange}
              options={filters.layout.options}
              label={filters.layout.label}
            />
          </div>
        )}
        {filters.capacity && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            {capacityFilterFlag ? (
              <CapacityFilterV2
                value={
                  filters.capacity.value
                    ? Number(filters.capacity.value)
                    : undefined
                }
                maxCapacity={maxCapacity}
                onChange={(capacity) => {
                  if (filters.capacity && filters.capacity.onChange) {
                    filters.capacity.onChange(
                      capacity ? String(capacity) : undefined
                    );
                  }
                }}
                onContactUs={onContactUs}
                partner={partner}
              />
            ) : (
              <MobileFilterItem
                value={filters.capacity.value}
                onSelect={filters.capacity.onChange}
                options={filters.capacity.options}
                label={filters.capacity.label}
              />
            )}
          </div>
        )}
        {filters.dateTime && (
          <div
            className={
              numberOfFilters > 1
                ? styles.mobileFilterItemWrapper
                : styles.singleMobileFilterItemWrapper
            }
          >
            {mrGalleryDateTimeFilterFlag ? (
              disableDateTimeFilter ? (
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      {fbt(
                        "Choose a city or location first to allow date and time filtering"
                      )}
                    </Tooltip>
                  }
                >
                  <div>
                    <MobileDateTimeFilterItemV2
                      value={filterValues}
                      onChange={onFilterValuesChange}
                      label={filters.dateTime.label}
                      openAt={partnerBookingHour?.openAt}
                      closedAt={partnerBookingHour?.closedAt}
                      timeSlotIncrement={
                        partnerBookingPolicy?.timeSlotIncrement
                      }
                      disabled
                    />
                  </div>
                </OverlayTrigger>
              ) : (
                <MobileDateTimeFilterItemV2
                  value={filterValues}
                  onChange={onFilterValuesChange}
                  label={filters.dateTime.label}
                  openAt={partnerBookingHour?.openAt}
                  closedAt={partnerBookingHour?.closedAt}
                  timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
                />
              )
            ) : (
              <MobileDateTimeFilterItem
                value={filterValues}
                onChange={onFilterValuesChange}
                label={filters.dateTime.label}
                openAt={partnerBookingHour?.openAt}
                closedAt={partnerBookingHour?.closedAt}
                timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
              />
            )}
          </div>
        )}
      </>
    );
  } else {
    let numberOfAppliedFilter = 0;
    if (filterValues.locationIDs) numberOfAppliedFilter++;
    if (filterValues.maxCapacity || filterValues.minCapacity)
      numberOfAppliedFilter++;
    if (filterValues.date) numberOfAppliedFilter++;

    const roomsFoundTitle = fbt(
      fbt.plural("room", foundResults, {
        name: "numberOfRooms",
        showCount: "yes",
        many: "rooms",
      }) + " found"
    );

    const disableDateTimeFilter =
      !internalFilterValues.cities?.length &&
      !internalFilterValues.locationIDs?.length &&
      locations.length > 2 &&
      !!filters.location;

    const dateTimeFilterValue: DateTimeValue | undefined =
      internalFilterValues.date
        ? {
            date: internalFilterValues.date,
            startTime: internalFilterValues.startTime,
            endTime: internalFilterValues.endTime,
          }
        : internalFilterValues.startDate && internalFilterValues.endDate
        ? {
            startDate: internalFilterValues.startDate,
            endDate: internalFilterValues.endDate,
          }
        : undefined;

    const handleShowResults = () => {
      setShowModal(false);
      onFilterValuesChange(internalFilterValues);
    };

    content = (
      <>
        <div className={styles.filterResult}>
          {foundResults !== totalRooms && numberOfAppliedFilter > 0
            ? roomsFoundTitle
            : fbt("Showing all rooms")}
        </div>
        <button
          className={`${styles.filterBtn} gallery-mobile-filter-button`}
          onClick={() => setShowModal(true)}
        >
          {fbt("Filters")}
          {numberOfAppliedFilter > 0 ? ` (${numberOfAppliedFilter})` : ""}
        </button>

        <Modal
          show={showModal}
          dialogClassName={styles.modalDialog}
          contentClassName={styles.modalContent}
          onHide={() => setShowModal(false)}
          scrollable
          fullscreen
        >
          <Modal.Header closeButton className={styles.modalHeader}>
            <b>{fbt("Filters")}</b>
          </Modal.Header>
          <Modal.Body>
            <ListGroup className="mr-gallery-mobile-filters-modal-list-group">
              {locationDropdownFlag && filters.location ? (
                <LocationFilterMobile
                  partner={partner}
                  locations={locations}
                  values={{
                    cities: internalFilterValues.cities,
                    locationIDs: internalFilterValues.locationIDs,
                  }}
                  onChangeLocations={(cities, locationIDs) =>
                    setInternalFilterValues((values) => ({
                      ...values,
                      cities: cities.length ? cities : undefined,
                      locationIDs: locationIDs.length ? locationIDs : undefined,
                    }))
                  }
                  highlightBorder={highlightLocation}
                />
              ) : (
                <>
                  {filters.state && (
                    <MobileModalFilterItem
                      value={internalFilterValues.state}
                      options={filters.state.options}
                      label={filters.state.label}
                      onSelect={(value) =>
                        setInternalFilterValues((v) => ({
                          ...v,
                          state: value,
                          city: undefined,
                          locationID: undefined,
                        }))
                      }
                    />
                  )}
                  {filters.city && (
                    <MobileModalFilterItem
                      value={internalFilterValues.city}
                      options={cityOptions}
                      label={filters.city.label}
                      placeholder={fbt("Any city")}
                      onSelect={(value) =>
                        setInternalFilterValues((v) => ({
                          ...v,
                          city: value,
                          locationID: undefined,
                        }))
                      }
                    />
                  )}
                  {filters.location && (
                    <MobileModalFilterItem
                      value={internalFilterValues.locationID}
                      options={locationOptions}
                      label={filters.location.label}
                      placeholder={fbt("Any location")}
                      onSelect={(value) =>
                        setInternalFilterValues((v) => ({
                          ...v,
                          locationID: value,
                        }))
                      }
                    />
                  )}
                </>
              )}
              {filters.layout && (
                <MobileModalFilterItem
                  value={internalFilterValues.layoutId}
                  options={filters.layout.options}
                  label={filters.layout.label}
                  onSelect={(value) =>
                    setInternalFilterValues((v) => ({
                      ...v,
                      layoutId: value,
                    }))
                  }
                />
              )}
              {filters.capacity &&
                (capacityFilterFlag ? (
                  <ModalCapacityFilterItem
                    value={internalFilterValues.minCapacity}
                    maxCapacity={maxCapacity}
                    onChange={(capacity) => {
                      setInternalFilterValues((v) => ({
                        ...v,
                        minCapacity: capacity || undefined,
                        maxCapacity: undefined,
                      }));
                    }}
                    onContactUs={onContactUs}
                    partner={partner}
                  />
                ) : (
                  <MobileModalFilterItem
                    value={internalFilterValues.minCapacity?.toString()}
                    options={filters.capacity.options}
                    label={filters.capacity.label}
                    onSelect={(value) =>
                      setInternalFilterValues((v) => ({
                        ...v,
                        minCapacity: Number(value) || undefined,
                        maxCapacity:
                          filters.capacity?.options.find(
                            (o) => o.value === value
                          )?.data.max || undefined,
                      }))
                    }
                  />
                ))}
              {filters.dateTime &&
                (mrGalleryDateTimeFilterFlag ? (
                  disableDateTimeFilter ? (
                    <OverlayTrigger
                      trigger="hover"
                      placement="bottom"
                      onToggle={(nextShow) => setHighlightLocation(nextShow)}
                      overlay={
                        <Tooltip>
                          {fbt(
                            "Choose a city or location first to allow date and time filtering"
                          )}
                        </Tooltip>
                      }
                    >
                      <div>
                        <MobileModalDateTimeFilterItemV2
                          value={dateTimeFilterValue}
                          onChange={(dateTimeValue) =>
                            setInternalFilterValues((v) => ({
                              ...v,
                              ...dateTimeValue,
                            }))
                          }
                          label={filters.dateTime.label}
                          openAt={partnerBookingHour?.openAt}
                          closedAt={partnerBookingHour?.closedAt}
                          timeSlotIncrement={
                            partnerBookingPolicy?.timeSlotIncrement
                          }
                          disabled
                        />
                      </div>
                    </OverlayTrigger>
                  ) : (
                    <MobileModalDateTimeFilterItemV2
                      value={dateTimeFilterValue}
                      onChange={(dateTimeValue) =>
                        setInternalFilterValues((v) => ({
                          ...v,
                          ...dateTimeValue,
                        }))
                      }
                      label={filters.dateTime.label}
                      openAt={partnerBookingHour?.openAt}
                      closedAt={partnerBookingHour?.closedAt}
                      timeSlotIncrement={
                        partnerBookingPolicy?.timeSlotIncrement
                      }
                    />
                  )
                ) : (
                  <MobileModalDateTimeFilterItem
                    value={internalFilterValues}
                    onChange={(dateTimeValue) =>
                      setInternalFilterValues((v) => ({
                        ...v,
                        ...dateTimeValue,
                      }))
                    }
                    label={filters.dateTime.label}
                    openAt={partnerBookingHour?.openAt}
                    closedAt={partnerBookingHour?.closedAt}
                    timeSlotIncrement={partnerBookingPolicy?.timeSlotIncrement}
                  />
                ))}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer className={styles.modalFooter}>
            <div className={styles.modalBtns}>
              <button
                className={styles.showResultsBtn}
                onClick={handleShowResults}
              >
                {fbt("Show results")}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <div className={`${styles.container} mr-gallery-mobile-filter-container`}>
      {content}
    </div>
  );
}

interface MobileFilterItemProps {
  value?: string;
  label: string;
  onSelect: (value?: string) => void;
  options: Option[];
}

function MobileFilterItem({
  value,
  label,
  onSelect,
  options,
}: MobileFilterItemProps) {
  return (
    <div className={styles.dropdownWrapper}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.dropdown)}
        onSelect={(value) => onSelect(value || undefined)}
      >
        <Dropdown.Toggle>
          {value ? (
            <div className={styles.displayValue}>
              {options.find((o) => o.value === value)?.label}
            </div>
          ) : (
            <div className={styles.placeholder}>{label}</div>
          )}
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {value && (
          <div className={styles.clearBtn} onClick={() => onSelect(undefined)}>
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu className={styles.dropdownMenu}>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={option.value === value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
}

interface ModalFilterItemProps {
  onSelect: (value?: string) => void;
  value?: string;
  placeholder?: string;
  options: Option[];
  label: string;
}

function MobileModalFilterItem(props: ModalFilterItemProps) {
  const { fbt } = useI18n();
  const { onSelect, value, placeholder = fbt("Any"), options, label } = props;

  return (
    <ListGroup.Item className={`${styles.filterItem} mobile-filter-item`}>
      <Dropdown
        className={classNames("checkout-box-picker", styles.modalDropdown)}
        onSelect={(value) => onSelect(value || undefined)}
      >
        <Dropdown.Toggle style={{ padding: 0 }}>
          <div className={styles.displayValue}>
            <div className={styles.label}>{label}</div>
            <Spacer size={8} />
            {value ? (
              <div>{options.find((o) => o.value === value)?.label}</div>
            ) : (
              <div className={styles.placeholder}>{placeholder}</div>
            )}
          </div>
          <div>
            <Icon name="fi-chevron-down" color="#82808E" />
          </div>
        </Dropdown.Toggle>
        {value && (
          <div className={styles.clearBtn} onClick={() => onSelect(undefined)}>
            <Icon name="go-x-circle" color="#82808E" size={16} />
          </div>
        )}
        <Dropdown.Menu>
          {options.map((option) => (
            <Dropdown.Item
              key={option.value}
              eventKey={option.value}
              active={option.value === value}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </ListGroup.Item>
  );
}
